// Copyright 2025, University of Maryland, All Rights Reserved
import { useCallback } from 'react'

import type { KnowledgeBaseItem } from '../types'
import { useKnowledgeBaseStore } from '../core/KnowledgeBaseProvider'
import { useKnowledgeBaseSettings } from './useKnowledgeBaseSettings'
import { useItemBrowser } from './useItemBrowser'
import { useKbItemSchema } from './useKbItemSchema'
import type { AddItemFormSubmission } from '../items/AddItemForm'
import type { ItemListUpdatePayload } from '../items/ItemList'
import { useAuth } from '@/subsystems/workspaces'

const LOG_SCOPE = '[knowledge-base:user-screen]'

interface UseKnowledgeBaseScreenArgs {
  config: unknown
  workspaceLabel: string
  itemsPerPage?: number
}

export function useKnowledgeBaseScreen({
  config,
  workspaceLabel,
  itemsPerPage = 5,
}: UseKnowledgeBaseScreenArgs) {
  const store = useKnowledgeBaseStore()
  const settings = useKnowledgeBaseSettings(config)
  const { isGlobalAdmin, isWorkspaceAdmin } = useAuth()
  const itemSchemaId =
    typeof store.settings.itemSchemaId === 'string' ? store.settings.itemSchemaId : null
  const { schema } = useKbItemSchema(itemSchemaId)

  const browser = useItemBrowser({
    items: store.data.items,
    filters: store.filters,
    setFilters: store.setFilters,
    itemsPerPage,
  })

  const readOnly = !isGlobalAdmin && !isWorkspaceAdmin
  const disableActions = store.loading || store.saving || readOnly

  const handleError = useCallback(
    (message: string) => {
      store.setError(message)
      console.warn(`${LOG_SCOPE} surface error`, {
        workspace: workspaceLabel,
        message,
      })
    },
    [store, workspaceLabel],
  )

  const handleCreateItem = useCallback(
    async (submission: AddItemFormSubmission) => {
      store.setError(null)

      console.info(`${LOG_SCOPE} create item request`, { workspace: workspaceLabel })

      try {
        await store.addItem({
          content: submission.draft.content,
          score: submission.draft.score,
          fields: submission.draft.fields ?? null,
        })
        console.info(`${LOG_SCOPE} create item success`, { workspace: workspaceLabel })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create item'
        store.setError(message)
        console.error(`${LOG_SCOPE} create item failure`, { workspace: workspaceLabel, error: message })
        throw err
      }
    },
    [store, workspaceLabel],
  )

  const handleUpdateItem = useCallback(
    async (item: KnowledgeBaseItem, payload: ItemListUpdatePayload) => {
      store.setError(null)

      console.info(`${LOG_SCOPE} update item request`, {
        workspace: workspaceLabel,
        itemId: item.id,
      })

      try {
        await store.updateItem({
          itemId: item.id,
          content: payload.content,
          score: payload.score,
          fields: payload.fields ?? null,
        })
        console.info(`${LOG_SCOPE} update item success`, {
          workspace: workspaceLabel,
          itemId: item.id,
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update item'
        store.setError(message)
        console.error(`${LOG_SCOPE} update item failure`, {
          workspace: workspaceLabel,
          itemId: item.id,
          error: message,
        })
        throw err
      }
    },
    [store, workspaceLabel],
  )

  const handleDeleteItem = useCallback(
    async (item: KnowledgeBaseItem) => {
      store.setError(null)
      console.info(`${LOG_SCOPE} delete item request`, {
        workspace: workspaceLabel,
        itemId: item.id,
      })
      try {
        await store.deleteItem(item.id)
        console.info(`${LOG_SCOPE} delete item success`, {
          workspace: workspaceLabel,
          itemId: item.id,
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete item'
        store.setError(message)
        console.error(`${LOG_SCOPE} delete item failure`, {
          workspace: workspaceLabel,
          itemId: item.id,
          error: message,
        })
        throw err
      }
    },
    [store, workspaceLabel],
  )

  return {
    store,
    settings,
    schema,
    browser,
    readOnly,
    disableActions,
    handleError,
    handleCreateItem,
    handleUpdateItem,
    handleDeleteItem,
  }
}
